"use client";

import { LinearGradient } from "@visx/gradient";
import { motion } from "framer-motion";
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

export interface FunnelStage {
  id: string;
  /** Label for the stage (e.g., "Product Viewed") */
  label: string;
  /** Numeric value for this stage */
  value: number;
  /** Optional secondary value (e.g., "+12% MoM") */
  secondaryValue?: string;
  /** Optional annotation badge */
  annotation?: string;
  /** Override color for this stage */
  color?: string;
}

export interface FunnelSeriesProps {
  /** Array of funnel stages ordered from top to bottom */
  data: FunnelStage[];
  /** Width of the chart */
  width?: number;
  /** Height of the chart */
  height?: number;
  /** Padding around the chart area */
  padding?: number;
  /** Gap between bars */
  barGap?: number;
  /** Format function for values */
  formatValue?: (value: number) => string;
  /** Format function for percentages */
  formatPercentage?: (value: number) => string;
  /** Single color to use for all bars (uses theme color if not provided) */
  color?: string;
  /** Show percentage scale on left */
  showScale?: boolean;
  /** Show overall conversion rate at top */
  showOverallConversion?: boolean;
  /** Label text for overall conversion (for internationalization) */
  overallConversionLabel?: string;
  /** Optional external active stage identifier */
  activeStageId?: string | null;
  /** Callback when a stage is clicked */
  onStageClick?: (stage: FunnelStage) => void;
  /** Callback when a stage is hovered */
  onStageHover?: (stage: FunnelStage | null) => void;
  /** Additional className */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
  /** Accessible label */
  "aria-label"?: string;
}

interface LayoutStage {
  stage: FunnelStage;
  index: number;
  y: number;
  barHeight: number;
  widthPercent: number;
  conversionRate: number;
  dropOff: number;
}

const defaultFormatValue = (value: number) => value.toLocaleString();
const defaultFormatPercentage = (value: number) => `${Math.round(value * 100)}%`;

export const FunnelSeries: React.FC<FunnelSeriesProps> = ({
  data,
  width = 800,
  height = 400,
  padding = 0,
  barGap = 30,
  formatValue = defaultFormatValue,
  formatPercentage = defaultFormatPercentage,
  color,
  showScale = true,
  showOverallConversion = true,
  overallConversionLabel = "Overall Conversion:",
  activeStageId,
  onStageClick,
  onStageHover,
  className,
  style,
  "aria-label": ariaLabel = "Funnel chart",
}) => {
  const [hoveredStage, setHoveredStage] = useState<string | null>(null);
  const [actualWidth, setActualWidth] = useState(width);
  const containerRef = useRef<HTMLDivElement>(null);
  const resolvedActiveStageId = activeStageId ?? hoveredStage;
  const gradientId = useId();
  const baseColor = color || "var(--color-viz-default)";

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setActualWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const { layout, overallConversion } = useMemo(() => {
    if (data.length === 0) {
      return { layout: [], overallConversion: 0 };
    }

    const maxValue = data[0].value;
    const overallConversionText = showOverallConversion ? 60 : 0;
    const availableHeight = height - padding * 2 - overallConversionText;
    const totalGaps = (data.length - 1) * barGap;
    const barHeight = (availableHeight - totalGaps) / data.length;

    const layout: LayoutStage[] = data.map((stage, index) => {
      const y = padding + overallConversionText + index * (barHeight + barGap);
      const widthPercent = maxValue === 0 ? 0 : stage.value / maxValue;
      const conversionRate = widthPercent;
      const previousValue = index === 0 ? null : data[index - 1].value;
      const dropOff = previousValue == null || previousValue === 0 ? 0 : 1 - stage.value / previousValue;

      return {
        stage,
        index,
        y,
        barHeight,
        widthPercent,
        conversionRate,
        dropOff,
      };
    });

    const overallConversion = maxValue === 0 ? 0 : data[data.length - 1].value / maxValue;

    return { layout, overallConversion, maxValue };
  }, [data, actualWidth, height, padding, barGap, showScale, showOverallConversion]);

  if (data.length === 0) {
    return (
      <div
        className={twMerge(
          "flex h-full min-h-[280px] w-full items-center justify-center rounded-lg border border-dashed border-gray-300 bg-background-surface text-sm font-medium text-text-muted dark:border-gray-700",
          className
        )}
        style={{ width, height, ...style }}
      >
        No funnel data available
      </div>
    );
  }

  const scaleWidth = showScale ? 60 : 0;
  const chartLeft = padding + scaleWidth;
  const chartWidth = actualWidth - padding * 2 - scaleWidth;

  return (
    <div
      ref={containerRef}
      className={twMerge("relative w-full overflow-hidden rounded-lg bg-background-surface", className)}
      style={{ height, ...style }}
      aria-label={ariaLabel}
      role="img"
    >
      <svg width="100%" height={height} className="relative">
        <defs>
          <LinearGradient id={`${gradientId}-bar`} from={baseColor} to={baseColor} toOpacity={0.85} />
          <LinearGradient
            id={`${gradientId}-bg`}
            from={baseColor}
            to={baseColor}
            fromOpacity={0.01}
            toOpacity={0.15}
            x1="0"
            y1="0"
            x2="1"
            y2="0"
          />
        </defs>

        {/* Overall conversion at top */}
        {showOverallConversion && (
          <text x={chartLeft} y={padding + 20} className="fill-text-body text-sm font-medium">
            <tspan className="fill-text-muted">{overallConversionLabel}</tspan> {formatPercentage(overallConversion)}
          </text>
        )}

        {/* Y-axis scale */}
        {showScale && (
          <g>
            {layout.map((item) => {
              const y = item.y + item.barHeight / 2;
              return (
                <text
                  key={item.stage.id}
                  x={padding + scaleWidth - 12}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="middle"
                  className="fill-text-caption text-xs"
                >
                  {formatPercentage(item.conversionRate)}
                </text>
              );
            })}
          </g>
        )}

        {/* Bars */}
        {layout.map((item) => {
          const isActive = resolvedActiveStageId === item.stage.id;
          const barWidth = chartWidth * item.widthPercent;

          return (
            <g key={item.stage.id}>
              {/* Background bar (full width, light) */}
              <rect
                x={chartLeft}
                y={item.y}
                width={chartWidth}
                height={item.barHeight}
                fill={`url(#${gradientId}-bg)`}
                rx={4}
              />

              {/* Foreground bar (actual data) */}
              <motion.rect
                x={chartLeft}
                y={item.y}
                width={barWidth}
                height={item.barHeight}
                fill={`url(#${gradientId}-bar)`}
                rx={4}
                initial={{ width: 0 }}
                animate={{ width: barWidth }}
                transition={{
                  duration: 0.6,
                  delay: item.index * 0.1,
                  ease: "easeOut",
                }}
                opacity={isActive || resolvedActiveStageId == null ? 1 : 0.5}
                style={{
                  cursor: onStageClick || onStageHover ? "pointer" : "default",
                  transition: "opacity 150ms ease",
                }}
                onMouseEnter={() => {
                  setHoveredStage(item.stage.id);
                  onStageHover?.(item.stage);
                }}
                onMouseLeave={() => {
                  setHoveredStage(null);
                  onStageHover?.(null);
                }}
                onClick={() => onStageClick?.(item.stage)}
              />

              {/* Background for value label on bar */}
              <rect
                x={chartLeft + 10}
                y={item.y + item.barHeight / 2 - 10}
                width={70}
                height={20}
                className="fill-background-surface stroke-border-default"
                strokeWidth={1}
                rx={4}
                style={{ pointerEvents: "none" }}
              />

              {/* Value label (centered in bar) */}
              <text
                x={chartLeft + 16}
                y={item.y + item.barHeight / 2 + 4}
                className="fill-text-heading text-xs font-semibold"
                style={{ pointerEvents: "none" }}
              >
                {formatValue(item.stage.value)}
              </text>

              {/* Stage label (above bar) */}
              <text x={chartLeft} y={item.y - 8} className="fill-text-muted text-xs" style={{ pointerEvents: "none" }}>
                <tspan>{item.index + 1}</tspan> {item.stage.label}
              </text>

              {/* Secondary value (if provided) */}
              {item.stage.secondaryValue && (
                <text
                  x={chartLeft + 16}
                  y={item.y - 24}
                  className="fill-text-muted text-xs"
                  style={{ pointerEvents: "none" }}
                >
                  {item.stage.secondaryValue}
                </text>
              )}

              {/* Drop-off indicator (between bars) */}
              {item.index > 0 && item.dropOff > 0 && (
                <text x={chartLeft + chartWidth} y={item.y - 8} textAnchor="end" className="fill-text-muted text-xs">
                  {formatPercentage(item.dropOff)} drop
                </text>
              )}

              {/* Annotation badge */}
              {item.stage.annotation && (
                <g>
                  <rect
                    x={chartLeft + barWidth + 8}
                    y={item.y + 8}
                    width={60}
                    height={24}
                    rx={3}
                    className="fill-background-muted"
                  />
                  <text x={chartLeft + barWidth + 16} y={item.y + 20} className="fill-text-caption text-xs font-medium">
                    {item.stage.annotation}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export type { FunnelStage as FunnelSeriesStage };
